import React from "react";

import {
  formatTeamViewsLastLogin,
  personRoleLabel,
  teamViewsAvatarColor,
  teamViewsRoleBadgeClass,
} from "./teamViewsHelpers";
import type { TeamViewsPerson } from "../../../Store/Reducers/TeamViewsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";

/** Team Views V3 person card variant (only shipped skin). */
export type PersonCardVariant = "v3";

type TeamViewsPersonCardProps = {
  person: TeamViewsPerson;
  showPortfolioHighlight?: boolean;
  variant?: PersonCardVariant;
  /** Logged-in user — show a green "You" chip beside the role badge. */
  isYou?: boolean;
  /** Privileged viewers only (VA / C-Level / ESL / executiveOps). */
  showLastLogin?: boolean;
  onClick?: (person: TeamViewsPerson) => void;
};

const TeamViewsPersonCard: React.FC<TeamViewsPersonCardProps> = ({
  person,
  showPortfolioHighlight = false,
  variant = "v3",
  isYou = false,
  showLastLogin = false,
  onClick,
}) => {
  const displayName = person.full_name || "Unknown";
  const roleLabel = personRoleLabel(person);
  const accountCount = person.account_count ?? 0;
  const accountLabel = `${accountCount} account${accountCount === 1 ? "" : "s"}`;
  const loginLabel = formatTeamViewsLastLogin(person.last_login_at);
  const isPortfolio =
    showPortfolioHighlight && Boolean(person.overlaps_viewer_portfolio);
  const cardClassName = `tv-card tv-card--${variant}${
    isPortfolio ? " tv-card-portfolio" : ""
  }`;

  const youBadge = isYou ? (
    <span className="tv-you-badge" title="You">
      You
    </span>
  ) : null;

  const roleBadge =
    roleLabel || isYou ? (
      <div className="tv-card-role-row">
        {roleLabel ? (
          <div
            className={`tv-card-role-badge ${teamViewsRoleBadgeClass(roleLabel)}`}
          >
            {roleLabel}
          </div>
        ) : null}
        {youBadge}
      </div>
    ) : null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(person);
    }
  };

  return (
    <div
      className={cardClassName}
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(person)}
      onKeyDown={handleKeyDown}
    >
      <div
        className="tv-card-accent"
        style={{ background: teamViewsAvatarColor(person) }}
        aria-hidden
      />
      <div className="tv-card-badge-slot">
        {/* Portfolio badge hidden while Show my team exists — restore if needed:
        {isPortfolio ? (
          <span className="tv-card-portfolio-badge">Your portfolio</span>
        ) : null}
        */}
      </div>
      <div
        className="tv-avatar"
        style={{ background: teamViewsAvatarColor(person) }}
      >
        {getInitials(displayName)}
      </div>
      <div className="tv-card-name">{displayName}</div>
      {roleBadge}
      <div className="tv-card-footer">
        {showLastLogin ? (
          <span
            className={`tv-card-meta tv-card-login${person.last_login_at ? "" : " tv-not-logged-in"}`}
          >
            {loginLabel}
          </span>
        ) : null}
        <span className="tv-card-accounts" aria-label={accountLabel}>
          <span className="tv-card-accounts-default">{accountLabel}</span>
          <span className="tv-card-accounts-cta" aria-hidden>
            View {accountCount} account{accountCount === 1 ? "" : "s"}
            <span className="tv-card-accounts-arrow">↗</span>
          </span>
        </span>
      </div>
    </div>
  );
};

export default TeamViewsPersonCard;
